import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import {
  type DefaultValues,
  FormProvider,
  type SubmitHandler,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import { Box } from '~/components/elements/Box'
import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import { exists } from '~/utils'

import { dialogInfoAtom } from '../../atoms'
import { useCloseDialogWithRefresh } from '../../hooks/useCloseDialogWithRefresh'
import { useCreateCustomerAttributesMutation } from '../../hooks/useCreateCustomerAttributesMutation'
import { useFilterGroupsToText } from '../../hooks/useFilterGroupsToText'

import { AllCheckbox } from './AllCheckbox'
import styles from './CreateInBulkSelectCustomerAttributesDialog.css'
import { Item } from './Item'
import { type InputValues, resolver } from './utils'

export const CreateInBulkSelectCustomerAttributesDialog = () => {
  const { t } = useTranslation()
  const resetDialogInfo = useResetAtom(dialogInfoAtom)

  const dialogInfo = useAtomValue(dialogInfoAtom)
  const filterGroupsArray = React.useMemo(
    () =>
      match(dialogInfo)
        .with(
          { dialogType: 'createInBulkSelectCustomerAttributes' },
          ({ filterGroupsArray }) => filterGroupsArray,
        )
        .run(),
    [dialogInfo],
  )
  const filterGroupsToText = useFilterGroupsToText()
  const defaultValues = React.useMemo<DefaultValues<InputValues>>(
    () => ({
      customerAttributes: filterGroupsArray.map((filterGroups) => ({
        name: filterGroupsToText(filterGroups),
        checked: true,
      })),
    }),
    [filterGroupsArray, filterGroupsToText],
  )
  const methods = useForm<InputValues>({ defaultValues, resolver })
  const {
    formState: { isValid },
    handleSubmit,
    watch,
  } = methods

  const customerAttributes = watch('customerAttributes')
  const checkedCount = customerAttributes.filter(R.prop('checked')).length

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()
  const createCustomerAttributesMutation = useCreateCustomerAttributesMutation({
    onSuccess: (data) => {
      // 名前の重複や絞り込み設定が無効な場合などcreateCustomerAttributeBulkはcustomerAttributesを返さない。作成できていないので記録しない。
      const createdCustomerAttributes =
        data.createCustomerAttributeBulk?.customerAttributes
      if (exists(createdCustomerAttributes)) {
        logEvent('Create customer attribute', {
          eventProperties: [
            { key: 'count', value: String(createdCustomerAttributes.length) },
          ],
        })
      }
      closeDialogWithRefresh()
    },
  })
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ customerAttributes }) => {
      createCustomerAttributesMutation.mutate(
        R.pipe(
          customerAttributes,
          R.zip(filterGroupsArray),
          R.filter(([customerAttribute]) => customerAttribute.checked),
          R.map(([customerAttribute, filterGroups]) => ({
            name: customerAttribute.name,
            filterGroups,
          })),
        ),
      )
    },
    [createCustomerAttributesMutation, filterGroupsArray],
  )

  return (
    <FormProvider {...methods}>
      <ExecuteCancelDialog
        disabledExecute={!isValid}
        executeButtonText={t('add_checkedcount_customized_segments', {
          checkedCount,
        })}
        executing={createCustomerAttributesMutation.isPending}
        mode="create"
        title={t('add_customized_segments_in_batch')}
        width="100%"
        onCancel={resetDialogInfo}
        onExecute={handleSubmit(handleValid)}
      >
        <VStack spacing={4}>
          <Typography variant="normal">
            {t('select_checkedcount_customized_segments', { checkedCount })}
          </Typography>

          <Box className={styles.grid}>
            <AllCheckbox />

            <Typography variant="normal">
              {t('customized_segments_name')}
            </Typography>

            <Typography variant="normal">{t('definition')}</Typography>

            <Box className={styles.separator} />

            {filterGroupsArray.map((filterGroups, index) => (
              <Item key={index} filterGroups={filterGroups} index={index} />
            ))}
          </Box>
        </VStack>
      </ExecuteCancelDialog>
    </FormProvider>
  )
}
